import {
  Form,
  Row,
  Col,
  DatePicker,
  Button,
  message,
  List,
  Tooltip,
  Card,
  Badge,
} from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  RedoOutlined,
} from "@ant-design/icons";
import TextArea from "antd/es/input/TextArea";
import React, { useState, useEffect } from "react";
import Select from "react-select";
import APICaller from "../../utils/APICaller";
import { useSelector } from "react-redux";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { SlideshowLightbox } from "lightbox.js-react";
import CustomLoader from "../../components/CustomLoader";

export default function PendingRequestDetail() {
  const location = useLocation();
  const navigate = useNavigate();
  const id = location.pathname?.split("/")?.[2];
  const [form] = Form.useForm();
  const { RangePicker } = DatePicker;
  let storeRole = useSelector((state) => state.auth.role);
  let storeToken = useSelector((state) => state.auth.accessToken);
  let storeLIUId = useSelector((state) => state.auth.lIUId);

  const [selectedValues, setSelectedValues] = useState([]);
  const [expenseCodes, setExpenseCodes] = useState([]);
  const [prevApproverNotSelected, setPrevApproverNotSelected] = useState(false);
  const [adminSelectedApprovers, setAdminSelectedApprovers] = useState([]);
  const [adminChangedExpenseCode, setAdminChangedExpenseCode] = useState({});

  const storeAccessTokenExpiresOn = useSelector(
    (state) => state.auth.accessTokenExpiresOn,
  );

  const decryptedEmpID = useSelector((state) => state.auth.empID);

  const [photoIndex, setPhotoIndex] = useState(0);
  const [projectCodes, setProjectCodes] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [expenseValues, setExpenseValues] = useState();
  const [checkboxes, setCheckboxes] = useState([]);
  const [expensesResponse, setExpensesResponse] = useState();
  const [showLoader, setShowLoader] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(
    process.env.REACT_APP_PENDING_STATUS.toUpperCase(),
  );
  const [reason, setReason] = useState(null);
  const [otherReason, setOtherReason] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const approversStatusCount = expenseValues?.approvers.filter(
    (approver) =>
      approver.status === process.env.REACT_APP_APPROVED_STATUS.toUpperCase(),
  ).length;

  const handleCheckboxChange = (index) => {
    setPrevApproverNotSelected(false);
    if (index > 0 && !checkboxes[index - 1]) {
      message.error(`Please check previous checkboxes first!`);
      setPrevApproverNotSelected(true);
      return;
    }

    // Update checkbox state
    const updatedCheckboxes = [...checkboxes];
    updatedCheckboxes[index] = !checkboxes[index];
    setCheckboxes(updatedCheckboxes);

    // Update selected values based on the current checkbox state
    const updatedSelectedValues =
      updatedCheckboxes &&
      updatedCheckboxes
        .map((checked, i) =>
          checked ? expenseValues?.approvers[i]?.additional?._id : null,
        )
        .filter((id) => id !== null); // Filter out null values

    handleStatus(storeRole, updatedSelectedValues);
  };

  // Disable dates after the current date
  const disableFutureDates = (current) => {
    return current && current.isAfter(moment().endOf("day"), "day");
  };

  // Update checkboxes when expenseValues.approvers changes
  useEffect(() => {
    if (expenseValues?.approvers) {
      // Initialize checkboxes based on the approvers' status
      const initialCheckboxes = expenseValues?.approvers.map(
        (approver) =>
          approver.status ===
          process.env.REACT_APP_APPROVED_STATUS.toUpperCase(),
      );
      setCheckboxes(initialCheckboxes);
    }
  }, [expenseValues]);

  const getExpenseDetail = () => {
    const endpoint = `/pending-requests/${id}`;
    const method = "GET";
    const payload = {};
    APICaller(
      storeRole,
      storeToken,
      endpoint,
      method,
      payload,
      storeAccessTokenExpiresOn,
    ).then((response) => {
      setExpenseValues(response);
      setExpensesResponse(response?.expensesDetails);

      form.setFieldsValue({
        empName: response?.empName,
        empID: response?.empID,
        departmentID: `${response?.departmentID?.name} - ${response?.departmentID?.code}`,
        expensePeriodFromDate: [
          response?.expensePeriodFromDate
            ? moment(response?.expensePeriodFromDate.split("T")[0])
            : null,
          response?.expensePeriodToDate
            ? moment(response?.expensePeriodToDate.split("T")[0])
            : null,
        ],
        businessPurpose: response?.businessPurpose,
      });
    });
  };

  // GetProjectCodes
  const getProjectCodes = () => {
    const endpoint = "/project-code/findAll";
    const method = "POST";
    const payload = {
      fetchWithoutPagination: "yes",
    };

    APICaller(
      storeRole,
      storeToken,
      endpoint,
      method,
      payload,
      storeAccessTokenExpiresOn,
    )
      .then((response) => {
        //const temp = [{ label: "N/A", value: null }];
        const temp = [];
        response?.response?.map((projectCode, index) => {
          temp.push({
            label: projectCode?.code + " - " + projectCode?.companyName,
            value: projectCode?._id,
          });
        });

        setProjectCodes(temp);
      })
      .catch((error) => {});
  };

  //Fetch expenseCodes
  const getExpenseCodes = () => {
    const endpoint = "/expense-code/findAll";
    const method = "POST";
    const payload = {
      fetchWithoutPagination: "yes",
    };
    APICaller(
      storeRole,
      storeToken,
      endpoint,
      method,
      payload,
      storeAccessTokenExpiresOn,
    )
      .then((response) => {
        const temp = [];
        response &&
          response?.response?.map((expenseCode, index) => {
            temp.push({
              label: expenseCode?.costCode + " - " + expenseCode?.name,
              value: expenseCode?._id,
            });
          });

        setExpenseCodes(temp);
      })
      .catch((error) => {});
  };

  const images =
    expenseValues?.expensesDetails?.flatMap((detail) =>
      detail.mediaInfo
        ?.filter(
          (file) =>
            file?.savedFileType === "jpeg" ||
            file?.savedFileType === "jpg" ||
            file?.savedFileType === "png",
        )
        ?.map(
          (file) => process.env.REACT_APP_BACKEND + "/" + file?.savedFilepath,
        ),
    ) || [];

  const handleImageClick = (filePath) => {
    const index = images.findIndex((img) => img === filePath);
    if (index !== -1) {
      setPhotoIndex(index);
      setIsOpen(true);
    }
  };
  const onFinish = () => {
    if (
      storeRole === process.env.REACT_APP_ADROLE &&
      prevApproverNotSelected === true
    ) {
      message.error("Please check previous checkboxes first!");
    } else if (
      storeRole === process.env.REACT_APP_ADROLE &&
      currentStatus === process.env.REACT_APP_APPROVED_STATUS.toUpperCase() &&
      expenseValues?.approvers.length > 1 &&
      adminSelectedApprovers.length === 0
    ) {
      //when there is more than one approver and it is not selected
      message.error("Please select first approver.");
    } else if (
      storeRole === process.env.REACT_APP_ADROLE &&
      currentStatus === process.env.REACT_APP_APPROVED_STATUS.toUpperCase() &&
      expenseValues?.approvers.length === 1 &&
      adminSelectedApprovers.length === 0
    ) {
      //when there is one approver and it is not selected
      message.error("Please select an approver.");
    } else if (
      storeRole === process.env.REACT_APP_ADROLE &&
      currentStatus === process.env.REACT_APP_APPROVED_STATUS.toUpperCase() &&
      approversStatusCount === 1 &&
      adminSelectedApprovers.length === 1 &&
      approversStatusCount != expenseValues?.approvers.length
    ) {
      //when there is one approver who approved and total number of approvers is not equals to approved approver
      message.error("Please select an approver.");
    } else if (
      currentStatus === process.env.REACT_APP_PENDING_STATUS.toUpperCase()
    ) {
      message.error("Please select the status.");
    } else if (
      currentStatus === process.env.REACT_APP_CANCELED_STATUS.toUpperCase() &&
      reason === null
    ) {
      message.error("Please provide the reason.");
    } else if (
      currentStatus === process.env.REACT_APP_SENTBACK_STATUS.toUpperCase() &&
      reason === null &&
      otherReason === null
    ) {
      message.error("Please provide the reason.");
    } else if (
      (currentStatus === process.env.REACT_APP_SENTBACK_STATUS.toUpperCase() ||
        currentStatus ===
          process.env.REACT_APP_CANCELED_STATUS.toUpperCase()) &&
      otherReason === "Others" &&
      reason === null
    ) {
      message.error("Others: Please provide the reason.");
    } else if (
      currentStatus === process.env.REACT_APP_CANCELED_STATUS.toUpperCase() &&
      reason === null
    ) {
      message.error("Please provide the reason.");
    } else {
      setShowLoader(true);
      const endpoint = `/pending-requests/${id}`;
      const method = "PUT";
      const payload = {
        role: storeRole,
        status: currentStatus,
        reason,
        projectCodeID: expenseValues?.projectCodeID?._id || null,
        historySource: "backend",
        historyAction: "update",
        historyUserID: storeLIUId,
        adminSelectedApprovers,
        adminChangedExpenseCode,
      };

      APICaller(
        storeRole,
        storeToken,
        endpoint,
        method,
        payload,
        storeAccessTokenExpiresOn,
      )
        .then((response) => {
          message.success("Expense updated successfully.");
          setShowLoader(false);
          navigate(`/${process.env.REACT_APP_DASHBOARD_PR_SEARCH_IN}`);
        })
        .catch((error) => {
          setShowLoader(false);
        });
    }
  };
  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
    setShowLoader(false);
  };

  const hasViewAccess =
    storeRole === process.env.REACT_APP_AROLE
      ? expenseValues &&
        expenseValues?.approvers.filter((expenseValue) => {
          return expenseValue?.userID === storeLIUId;
        })
      : "";

  const showStatusDropdown =
    storeRole === process.env.REACT_APP_AROLE
      ? expenseValues &&
        expenseValues?.approvers.filter((expenseValue) => {
          return (
            expenseValue?.userID === storeLIUId &&
            expenseValue?.status ===
              process.env.REACT_APP_PENDING_STATUS.toUpperCase()
          );
        })
      : "";

  const getStatusBadge = (status, reason) => {
    switch (status) {
      case "APPROVED":
        return (
          <Badge
            status="success"
            text="Approved"
            icon={<CheckCircleOutlined />}
            className="approved-badge"
          />
        );
      case "PENDING":
        return (
          <Badge
            status="warning"
            text="Pending Approval: Request Sent"
            icon={<ClockCircleOutlined />}
            className="pending-badge"
          />
        );
      case "CANCELED":
        return (
          <Badge
            status="error"
            text={
              <span>
                <strong>Canceled</strong>: {reason}
              </span>
            }
            icon={<CloseCircleOutlined />}
            className="canceled-badge"
          />
        );
      case "SENTBACK":
        return (
          <Badge
            status="processing"
            text={
              <span>
                <strong>Sent Back</strong>: {reason}
              </span>
            }
            icon={<RedoOutlined />}
            className="sentBack-badge"
          />
        );
      default:
        return (
          <Badge
            status="default"
            text="Awaiting previous Approver's Approval"
            icon={<ExclamationCircleOutlined />}
          />
        );
    }
  };

  const handleStatus = (source, checkedValues) => {
    if (source === process.env.REACT_APP_AROLE) {
      // Filter to keep only those values whose status allows them to be checked
      const filteredValues =
        checkedValues &&
        checkedValues.filter((value) => {
          const approver = expenseValues?.approvers.find(
            (approver) => approver?.additional?._id === value,
          );

          // Only include if the approver's status is not "APPROVED"
          return (
            approver &&
            approver.status !==
              process.env.REACT_APP_APPROVED_STATUS.toUpperCase()
          );
        });
    }
    if (source === process.env.REACT_APP_ADROLE) {
      setAdminSelectedApprovers(checkedValues);
      setSelectedValues(checkedValues);
    }
  };

  const commonStatusValues = [
    {
      label: "Approved",
      value: process.env.REACT_APP_APPROVED_STATUS?.toUpperCase() || "APPROVED",
    },
    {
      label: "Sent Back",
      value:
        process.env.REACT_APP_SENTBACK_STATUS?.toUpperCase() || "SENT_BACK",
    },
    {
      label: "Canceled",
      value: process.env.REACT_APP_CANCELED_STATUS?.toUpperCase() || "CANCELED",
    },
  ];

  const acknowledgerID =
    expenseValues?.approvers &&
    expenseValues?.approvers.length === 1 &&
    expenseValues?.approvers[0]?.userID;

  const expenseCodesList = [
    { _id: "1", costCode: "CC001", name: "Travel" },
    { _id: "2", costCode: "CC002", name: "Office Supplies" },
  ];

  // Effect to set default selected values based on some criteria
  useEffect(() => {
    // Assuming you want to select specific approvers by their userID
    const defaultSelected = expenseValues?.approvers
      .filter(
        (approver) =>
          approver.status ===
          process.env.REACT_APP_APPROVED_STATUS.toUpperCase(),
      )
      .map((approver) => approver.userID); // Map to userIDs

    if (defaultSelected && defaultSelected.length > 0) {
      setSelectedValues(defaultSelected);
      setAdminSelectedApprovers(defaultSelected);
    }
  }, [expenseValues]);

  useEffect(() => {
    getExpenseDetail();
    getExpenseCodes();
    getProjectCodes();
  }, []);

  //Approver - a1 / a2 id with status is pending or admin
  useEffect(() => {
    // Only check conditions once the data is available
    if (storeRole && expenseValues) {
      if (decryptedEmpID !== expenseValues?.empID) {
        if (hasViewAccess && hasViewAccess.length === 0) {
          setShouldRedirect(true);
        }
      }
      setIsLoading(false);
    }
  }, [storeRole, expenseValues]);

  // if (isLoading) {
  //   return <CustomLoader showLoader={showLoader} />; // Show a loading state while checking conditions
  // }

  if (shouldRedirect) {
    return <Navigate to="/dashboard" />;
  }

  return (
    <div className="container">
      <CustomLoader showLoader={showLoader} />
      <div className="card bgi-no-repeat card-stretch gutter-b border-2 rounded-lg p-0">
        <div className="border-2 card-header p-6 d-flex justify-content-between align-items-center">
          <h3 className="card-title m-0 p-0 border-2 font-weight-bolder text-dark font-size-lg">
            Pending Expense Details
          </h3>
        </div>
        <div className="card-body p-3">
          <Form
            layout="vertical"
            autoComplete="off"
            className="form-validation-start"
            form={form}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
          >
            <Row gutter={[12, 12]}>
              <Col sm={8}>
                <Form.Item className="m-0" label="Employee ID" name="empID">
                  <input
                    placeholder="Id"
                    className="form-control rounded-md"
                    defaultValue={expenseValues?.empID}
                    disabled
                  />
                </Form.Item>
              </Col>

              <Col sm={8}>
                <Form.Item className="m-0" label="Employee Name" name="empName">
                  <input
                    defaultValue={expenseValues?.empName}
                    disabled
                    placeholder="Employee Name"
                    className="form-control rounded-md"
                  />
                </Form.Item>
              </Col>

              <Col sm={8}>
                <Form.Item
                  className="m-0"
                  label="Department"
                  name="departmentID"
                >
                  <input
                    placeholder="Department"
                    className="form-control rounded-md"
                    disabled
                    defaultValue={`${expenseValues?.departmentID?.name} - ${expenseValues?.departmentID?.code}`}
                  />
                </Form.Item>
              </Col>

              <Col sm={8}>
                <Form.Item
                  className="m-0"
                  label={
                    <span>
                      <span className="mandatory-symbol">*</span>Expense Period:
                      (From - To)
                    </span>
                  }
                  name="expensePeriodFromDate"
                >
                  <RangePicker
                    // disabledDate={disableFutureDates}
                    className="form-control rounded-md d-inline-flex"
                    format="DD-MM-YYYY"
                    value={[
                      expenseValues?.expensePeriodFromDate
                        ? moment(expenseValues?.expensePeriodFromDate)
                        : null,
                      expenseValues?.expensePeriodToDate
                        ? moment(expenseValues?.expensePeriodToDate)
                        : null,
                    ]}
                    disabled
                  />
                </Form.Item>
              </Col>

              <Col sm={8}>
                <Form.Item
                  className="m-0"
                  label={
                    <span>
                      <span className="mandatory-symbol">*</span>Project Code
                    </span>
                  }
                  name="projectCodeID"
                >
                  <input
                    className="form-control rounded-md"
                    disabled
                    defaultValue={
                      expenseValues?.projectCodeID?.code &&
                      expenseValues?.projectCodeID?.companyName
                        ? expenseValues?.projectCodeID?.code +
                          " - " +
                          expenseValues?.projectCodeID?.companyName
                        : "N/A"
                    }
                  />
                </Form.Item>
              </Col>

              {expenseValues?.natureOfExpense && (
                <Col sm={8}>
                  <label className="mb-3" name="natureOfExpense">
                    <span className="mandatory-symbol">*</span>Nature of Expense
                  </label>
                  <input
                    className="form-control rounded-md"
                    disabled
                    defaultValue={expenseValues?.natureOfExpense}
                  />
                </Col>
              )}

              <Col sm={8}>
                <Form.Item
                  className="m-0"
                  label={
                    <span>
                      <span className="mandatory-symbol">*</span>Business
                      Purpose
                    </span>
                  }
                  name="businessPurpose"
                >
                  <textarea disabled className="form-control rounded-md">
                    {expenseValues?.businessPurpose}
                  </textarea>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[24, 24]}>
              <Col xs={24}>
                <div className="border-top my-10"></div>
              </Col>
            </Row>

            <Row gutter={[24, 24]}>
              <Col xs={24}>
                <div className="border-top my-10"></div>
              </Col>
            </Row>

            {/*Repeater Area*/}
            <div className="repeter border border-light-dark mb-5 p-3 repeter rounded-sm">
              <Row gutter={[12, 12]} className="m-0">
                <Col sm={3} className="repeat-label">
                  <label className="mb-0">
                    <span className="mandatory-symbol">*</span>Date
                  </label>
                </Col>
                <Col sm={3} className="repeat-label">
                  <label className="mb-0">
                    <span className="mandatory-symbol">*</span>Description
                  </label>
                </Col>
                <Col sm={3} className="repeat-label">
                  <label className="mb-0">
                    <span className="mandatory-symbol">*</span>Department
                  </label>
                </Col>
                <Col sm={4} className="repeat-label">
                  <label className="mb-0">
                    <span className="mandatory-symbol">*</span>Expense Code
                  </label>
                </Col>
                <Col sm={3} className="repeat-label">
                  <label className="mb-0">
                    <span className="mandatory-symbol">*</span>Currency
                  </label>
                </Col>
                <Col sm={3} className="repeat-label">
                  <label className="mb-0">
                    <span className="mandatory-symbol">*</span>Cost
                  </label>
                </Col>
                <Col sm={2} className="repeat-label">
                  <label className="mb-0">Upload</label>
                </Col>
              </Row>

              {expenseValues?.expensesDetails &&
                expenseValues?.expensesDetails.map((detail, index) => {
                  return (
                    <Row gutter={[12, 12]} className="mb-4" key={index}>
                      <Col sm={3}>
                        <span className="form-control rounded-md customDisabled">
                          {moment
                            .utc(detail?.date)
                            .local()
                            .format("DD-MM-YYYY")}
                        </span>
                      </Col>

                      <Col sm={3}>
                        <span className="form-control h-auto rounded-md customDisabled">
                          {detail?.description}
                        </span>
                      </Col>

                      <Col sm={3}>
                        <span className="form-control h-auto rounded-md customDisabled">
                          {`${detail.departmentID?.name} - ${detail.departmentID?.code}`}
                        </span>
                      </Col>

                      <Col sm={4}>
                        <div
                          title={`${detail.expenseCodeID?.costCode} - ${detail.expenseCodeID?.name} -- ${detail.expenseCodeID}`}
                        >
                          {storeRole === process.env.REACT_APP_ADROLE &&
                          !expenseValues?.isAdminChangedExpenseCode ? (
                            <Select
                              value={
                                adminChangedExpenseCode[index]?.new || {
                                  label: `${detail.expenseCodeID?.costCode} - ${detail.expenseCodeID?.name}`,
                                  value: detail.expenseCodeID?._id,
                                }
                              }
                              onChange={
                                (value) => {
                                  setAdminChangedExpenseCode((prev) => ({
                                    ...prev,
                                    [index]: {
                                      old: {
                                        label: `${detail.expenseCodeID?.costCode} - ${detail.expenseCodeID?.name}`,
                                        value: detail.expenseCodeID?._id,
                                      },
                                      new: value,
                                    },
                                  }));
                                }
                                //handleChange(index, "expenseCodeID", value)
                              }
                              className="rounded-md"
                              options={expenseCodes}
                            />
                          ) : (
                            <span className="form-control h-auto rounded-md customDisabled">
                              {`${detail.expenseCodeID?.costCode} - ${detail.expenseCodeID?.name}`}
                            </span>
                          )}
                        </div>
                      </Col>

                      <Col sm={3}>
                        <span className="form-control rounded-md customDisabled">
                          {detail.currencyID?.code
                            ? detail.currencyID?.code
                            : "N/A"}
                        </span>
                      </Col>

                      <Col sm={3}>
                        <span className="form-control rounded-md customDisabled">
                          {detail.cost}
                        </span>
                      </Col>

                      <Col sm={2}>
                        {detail?.mediaInfo.map((file, index) => (
                          <div
                            className="symbol symbol-100 symbol-light mr-3 h-65 float-left border border-light-dark"
                            key={index}
                          >
                            {file?.savedFileType === "jpeg" ||
                            file?.savedFileType === "jpg" ||
                            file?.savedFileType === "png" ? (
                              <SlideshowLightbox>
                                <img
                                  key={index}
                                  src={`${process.env.REACT_APP_BACKEND}/${file?.savedFilepath}`}
                                  alt="media"
                                  width="100"
                                  height="100"
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    handleImageClick(
                                      `${process.env.REACT_APP_BACKEND}/${file?.savedFilepath}`,
                                    )
                                  }
                                />
                              </SlideshowLightbox>
                            ) : (
                              <div className="align-items-center d-flex h-65px justify-content-center w-65px">
                                <a
                                  href={
                                    process.env.REACT_APP_BACKEND +
                                    "/" +
                                    file?.savedFilepath
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <i className="flaticon-file-2 uploadIcons"></i>
                                  {/* {file?.savedFileName} */}
                                </a>
                              </div>
                            )}
                          </div>
                        ))}
                      </Col>
                    </Row>
                  );
                })}
            </div>
            {/*Repeater Area*/}

            <Row gutter={[24, 24]}>
              <Col sm={12}>
                {/* Card for Approvers */}
                <Card
                  title="Approvers Status"
                  bordered={true}
                  style={{ marginBottom: "10px" }}
                >
                  {expenseValues?.approvers && (
                    <List
                      itemLayout="horizontal"
                      dataSource={expenseValues?.approvers}
                      renderItem={(approver, index) => (
                        <List.Item>
                          <List.Item.Meta
                            title={
                              <>
                                <Tooltip
                                  title={`Approver ${index + 1}: ${
                                    approver?.additional?.name
                                  }`}
                                >
                                  <span>
                                    <strong>Approver Name: </strong>
                                    {approver?.additional?.name}
                                  </span>
                                </Tooltip>
                              </>
                            }
                            description={getStatusBadge(
                              approver?.status,
                              approver?.reason,
                            )}
                          />
                        </List.Item>
                      )}
                    />
                  )}
                </Card>
              </Col>

              <Col sm={12}>
                {/* Card for Admin Actions */}
                <Card title="Admin Action Status" bordered={true}>
                  {expenseValues?.adminApprover &&
                  expenseValues?.adminApprover.length > 0 ? (
                    <List
                      itemLayout="horizontal"
                      dataSource={expenseValues?.adminApprover}
                      renderItem={(adminApprover, index) => (
                        <List.Item>
                          <List.Item.Meta
                            title={
                              <>
                                <Tooltip
                                  title={`Approver: ${adminApprover?.userID?.name}`}
                                >
                                  <span>
                                    <strong>Approver Name: </strong>
                                    {adminApprover?.userID?.name}
                                  </span>
                                </Tooltip>
                              </>
                            }
                            description={getStatusBadge(
                              adminApprover?.status,
                              adminApprover?.reason,
                            )}
                          />
                        </List.Item>
                      )}
                    />
                  ) : (
                    <div className="admin-status-awaiting">
                      <span>
                        Admin action will take place after all approvers
                        complete their approvals.
                      </span>
                    </div>
                  )}
                </Card>
              </Col>
            </Row>

            {/*Acknowledgement*/}
            {expenseValues?.seekAcknowledgment?.[0]?.ackMessage !== null && (
              <Col sm={24}>
                <Card
                  title="Acknowledgement Received"
                  bordered={true}
                  className="mb-10"
                >
                  {expenseValues?.seekAcknowledgment?.[0]?.ackMessage}
                </Card>
              </Col>
            )}

            {expenseValues?.seekAcknowledgment?.[0]?.sentAt !== null &&
            expenseValues?.seekAcknowledgment?.[0]?.ackMessage === null ? (
              <Col sm={24}>
                <Card
                  title="Seek Acknowledgement"
                  bordered={true}
                  className="mb-10"
                >
                  <span>Request Sent</span>
                </Card>
              </Col>
            ) : (
              (storeRole === process.env.REACT_APP_ADROLE ||
                (showStatusDropdown && showStatusDropdown.length > 0)) && (
                <>
                  <Card
                    title={
                      storeRole === process.env.REACT_APP_ADROLE
                        ? "Manage Approvers & Request Status"
                        : "Manage Request Status"
                    }
                    bordered={true}
                  >
                    <div className="">
                      <Row gutter={[24, 24]}>
                        <Col sm={6}>
                          <label className="font-weight-bolder font-size-lg">
                            Status
                          </label>
                          <Select
                            className="rounded-md"
                            placeholder="Select options"
                            options={
                              (expenseValues?.acknowledgerID !==
                                acknowledgerID && approversStatusCount) ===
                                expenseValues?.approvers.length &&
                              storeRole === process.env.REACT_APP_ADROLE &&
                              expenseValues?.seekAcknowledgment[0]
                                ?.ackMessage === null
                                ? [
                                    {
                                      label: "Seek Acknowledgement",
                                      value:
                                        process.env.REACT_APP_SEEK_ACK_STATUS.replace(
                                          " ",
                                          "",
                                        ).toUpperCase(),
                                    },
                                    ...commonStatusValues,
                                  ]
                                : commonStatusValues
                            }
                            onChange={(item) => {
                              setCurrentStatus(item.value);
                              setReason(null);
                              setOtherReason(null);
                            }}
                          />
                        </Col>

                        {(currentStatus ===
                          process.env.REACT_APP_SENTBACK_STATUS.toUpperCase() ||
                          currentStatus ===
                            process.env.REACT_APP_CANCELED_STATUS.toUpperCase()) && (
                          <>
                            {currentStatus ===
                              process.env.REACT_APP_SENTBACK_STATUS.toUpperCase() && (
                              <Col sm={6}>
                                <label className="font-weight-bolder font-size-lg">
                                  <span className="mandatory-symbol">* </span>
                                  Reason
                                </label>
                                <Select
                                  className="rounded-md"
                                  placeholder="Select options"
                                  options={[
                                    {
                                      label: "Incorrect Information Provided",
                                      value: "Incorrect Information Provided",
                                    },
                                    {
                                      label: "Invalid Document Uploaded",
                                      value: "Invalid Document Uploaded",
                                    },
                                    { label: "Others", value: "Others" },
                                  ]}
                                  onChange={(item) => {
                                    setOtherReason(item.value);
                                    if (item.value === "Others") {
                                      setReason(null);
                                    } else {
                                      setReason(item.value);
                                    }
                                  }}
                                />
                              </Col>
                            )}
                            {(currentStatus ===
                              process.env.REACT_APP_CANCELED_STATUS.toUpperCase() ||
                              otherReason === "Others") && (
                              <Col sm={6}>
                                <label className="font-weight-bolder font-size-lg">
                                  <span className="mandatory-symbol">* </span>
                                  {currentStatus ===
                                  process.env.REACT_APP_CANCELED_STATUS.toUpperCase()
                                    ? "Reason"
                                    : "Others"}
                                </label>
                                <TextArea
                                  className="rounded-md"
                                  placeholder="Please enter the reason"
                                  onChange={(e) => {
                                    setReason(e.target.value);
                                  }}
                                />
                              </Col>
                            )}
                          </>
                        )}
                      </Row>
                    </div>
                    {storeRole === process.env.REACT_APP_ADROLE &&
                      currentStatus ===
                        process.env.REACT_APP_APPROVED_STATUS.toUpperCase() && (
                        <>
                          <div className="mt-5">
                            <Row gutter={[24, 24]}>
                              <Col sm={12}>
                                {/* Approver Section */}
                                <div className="approver-section">
                                  <div className="font-size-lg">
                                    Select Approvers
                                  </div>

                                  <div className="selectedApp">
                                    {expenseValues?.approvers.map(
                                      (approver, index) => {
                                        const isDisabled =
                                          approver.status ===
                                          process.env.REACT_APP_APPROVED_STATUS.toUpperCase();
                                        return (
                                          <label
                                            key={index}
                                            className={
                                              isDisabled ? "disabled-label" : ""
                                            }
                                          >
                                            <span className="checkbox-container">
                                              <input
                                                type="checkbox"
                                                checked={
                                                  checkboxes[index] || false
                                                }
                                                onChange={() => {
                                                  handleCheckboxChange(index);
                                                }}
                                                disabled={isDisabled} // Disable based on status
                                              />
                                            </span>
                                            <span>
                                              {approver?.additional?.name}
                                            </span>
                                          </label>
                                        );
                                      },
                                    )}
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        </>
                      )}

                    <div className="text-right mt-5">
                      <Button
                        type="primary"
                        htmlType="submit"
                        className=" btn font-size-h5 font-weight-bold mx-auto px-10 py-3 redbutton rounded-lg text-white"
                      >
                        Update
                      </Button>
                    </div>
                  </Card>
                </>
              )
            )}
          </Form>
        </div>
      </div>
    </div>
  );
}
